import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, storage } from "../lib/api";
import VenueMap from "../components/VenueMap";
import { Plus, X, Save, GripVertical } from "lucide-react";

export default function RouteBuilder() {
  const navigate = useNavigate();
  const [allVenues, setAllVenues] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [difficulty, setDifficulty] = useState("facil");
  const [duration, setDuration] = useState(3);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.listVenues().then(setAllVenues).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allVenues.filter((v) =>
      !q || v.name.toLowerCase().includes(q) || v.neighborhood.toLowerCase().includes(q)
    );
  }, [allVenues, search]);

  const selectedVenues = useMemo(
    () => selectedIds.map((id) => allVenues.find((v) => v.id === id)).filter(Boolean),
    [selectedIds, allVenues]
  );

  const toggle = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const move = (idx, dir) => {
    setSelectedIds((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const save = async () => {
    if (!name.trim() || selectedIds.length < 2) return;
    setSaving(true);
    try {
      const route = await api.createRoute({
        name,
        description,
        venue_ids: selectedIds,
        difficulty,
        duration_hours: Number(duration),
        is_official: false,
        cover_image: selectedVenues[0]?.cover_photo,
        created_by: storage.getHandle() || "anonimo",
      });
      navigate(`/rutas/${route.id}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8" data-testid="route-builder-page">
      <header className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">Crear ruta</div>
        <h1 className="mt-1 font-display text-5xl uppercase tracking-tighter leading-none">Arma tu ruta</h1>
        <p className="mt-3 text-[#A3A3A3]">Selecciona al menos 2 paradas, ordena, ponle nombre y guarda.</p>
      </header>

      <div className="grid lg:grid-cols-[1fr_420px] gap-6">
        {/* Map + selected sequence */}
        <main className="space-y-4">
          <VenueMap venues={selectedVenues} numbered showLine height={420} />

          <div>
            <h3 className="font-display text-2xl uppercase tracking-tight mb-2">Paradas seleccionadas ({selectedVenues.length})</h3>
            {selectedVenues.length === 0 ? (
              <div className="border-2 border-dashed border-[#333] p-6 text-center font-mono text-xs uppercase tracking-widest text-[#A3A3A3]">
                Sin paradas todavía. Agrega desde la lista →
              </div>
            ) : (
              <div className="space-y-2">
                {selectedVenues.map((v, i) => (
                  <div key={v.id} className="flex items-center gap-3 bg-[#1A1A1A] border-2 border-[#333] p-3" data-testid={`selected-venue-${i}`}>
                    <GripVertical size={14} className="text-[#A3A3A3]" />
                    <div className="w-8 h-8 bg-[#FEBA01] text-black flex items-center justify-center font-display text-base">{i + 1}</div>
                    <div className="flex-1">
                      <div className="font-display uppercase">{v.name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">{v.neighborhood}</div>
                    </div>
                    <div className="flex flex-col">
                      <button onClick={() => move(i, -1)} className="text-[#A3A3A3] hover:text-[#FEBA01] text-xs" data-testid={`move-up-${i}`}>▲</button>
                      <button onClick={() => move(i, 1)} className="text-[#A3A3A3] hover:text-[#FEBA01] text-xs" data-testid={`move-down-${i}`}>▼</button>
                    </div>
                    <button onClick={() => toggle(v.id)} className="text-[#FF3333] hover:text-[#FEBA01]" data-testid={`remove-${i}`}>
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-[#1A1A1A] border-2 border-[#333] p-5 space-y-4">
            <h3 className="font-display text-2xl uppercase tracking-tight">Detalles de la ruta</h3>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">Nombre</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Mi Ruta Brutal"
                className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2 text-[#F5F5F5]"
                data-testid="route-name-input"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">Descripción</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="¿Por qué vale la pena tu ruta?"
                className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2 text-[#F5F5F5]"
                data-testid="route-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">Dificultad</label>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2 text-[#F5F5F5] font-mono uppercase text-sm"
                  data-testid="route-difficulty-select"
                >
                  <option value="facil">Fácil</option>
                  <option value="intermedio">Intermedio</option>
                  <option value="hardcore">Hardcore</option>
                </select>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">Duración (h)</label>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2 text-[#F5F5F5]"
                  data-testid="route-duration-input"
                />
              </div>
            </div>
            <button
              onClick={save}
              disabled={saving || !name.trim() || selectedIds.length < 2}
              className="btn-brutal w-full bg-[#FEBA01] text-black font-display text-lg uppercase py-3 shadow-brutal-white disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="route-save-btn"
            >
              <Save size={18} /> {saving ? "Guardando…" : "Guardar ruta"}
            </button>
          </div>
        </main>

        {/* Available venues list */}
        <aside>
          <div className="bg-[#1A1A1A] border-2 border-[#333] p-4 sticky top-20 max-h-[calc(100vh-100px)] overflow-y-auto">
            <h3 className="font-display text-xl uppercase tracking-tight mb-3">Locales disponibles</h3>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="BUSCAR…"
              className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2 mb-3 font-mono uppercase text-xs text-[#F5F5F5] tracking-widest"
              data-testid="builder-search-input"
            />
            <div className="space-y-2">
              {filtered.map((v) => {
                const isSel = selectedIds.includes(v.id);
                return (
                  <button
                    key={v.id}
                    onClick={() => toggle(v.id)}
                    data-testid={`builder-venue-${v.slug}`}
                    className={`w-full text-left flex items-center gap-3 border-2 p-2 transition-colors ${
                      isSel ? "border-[#FEBA01] bg-[#FEBA01]/10" : "border-[#333] hover:border-[#FEBA01]"
                    }`}
                  >
                    {v.cover_photo && (
                      <img src={v.cover_photo} alt="" className="w-12 h-12 object-cover" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-sm uppercase truncate">{v.name}</div>
                      <div className="font-mono text-[9px] uppercase tracking-widest text-[#A3A3A3]">{v.neighborhood}</div>
                    </div>
                    <div className={`w-7 h-7 flex items-center justify-center border-2 ${isSel ? "bg-[#FEBA01] text-black border-[#FEBA01]" : "border-[#333] text-[#A3A3A3]"}`}>
                      {isSel ? "✓" : <Plus size={12} />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
